import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheckCircle,
  faCloudUploadAlt,
  faExclamationTriangle,
  faEye,
  faHistory,
  faInfoCircle,
  faTimes,
  faTimesCircle,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { uploadSettlementBatchDepoOr } from "../../services/settlementApi";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];

const isAllowedFile = (file) => {
  const name = (file?.name || "").toLowerCase();
  const extension = name.includes(".") ? name.split(".").pop() : "";
  return ALLOWED_EXTENSIONS.includes(extension);
};

const DepoOrBatchUploadPage = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const totalSizeLabel = useMemo(() => {
    const totalBytes = files.reduce((sum, file) => sum + (file.size || 0), 0);
    return `${(totalBytes / (1024 * 1024)).toFixed(2)} MB`;
  }, [files]);

  const pushValidFiles = (selectedFiles) => {
    if (!selectedFiles?.length) return;

    const next = [];
    for (const file of selectedFiles) {
      if (!isAllowedFile(file)) {
        toast.error(`Invalid file type: ${file.name}. Allowed: PDF/JPG/JPEG/PNG`);
        continue;
      }

      if ((file.size || 0) > MAX_FILE_BYTES) {
        toast.error(`File exceeds 10 MB: ${file.name}`);
        continue;
      }

      next.push(file);
    }

    if (!next.length) return;

    setFiles((prev) => {
      const existingKey = new Set(prev.map((f) => `${f.name}__${f.size}`));
      const unique = next.filter((f) => !existingKey.has(`${f.name}__${f.size}`));
      return [...prev, ...unique];
    });
    setUploadResult(null);
  };

  const handleSelect = (event) => {
    pushValidFiles(Array.from(event.target.files || []));
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    pushValidFiles(Array.from(event.dataTransfer.files || []));
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
    setUploadResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!files.length) {
      toast.error("Please select at least one file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files[]", file));

      const response = await uploadSettlementBatchDepoOr(formData);
      const data = response.data || {};

      setUploadResult({
        success: true,
        uploadId: data.upload_id,
        uploadedCount: data.uploaded_count || files.length,
        message: data.message || "Upload started.",
      });

      toast.success("Deposit Slip / OR files uploaded. Processing started.");
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
    } catch (error) {
      const message = error.response?.data?.message || "Upload failed";
      setUploadResult({ success: false, message });
      toast.error(typeof message === "string" ? message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">Deposit Slip / OR Files Upload</h1>
          <p className="text-gray-600">Batch upload files using v1 naming convention (DS_/OR_ prefix)</p>
        </div>
        <button
          onClick={() => navigate("/settlement-list")}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-100"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Bact to Settlement List
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-5">
          <h3 className="text-base font-bold text-gray-800 mb-3">Filename Rules</h3>
          <div className="space-y-2 text-sm">
            <div className="p-3 border rounded bg-blue-50 border-blue-200">
              <p className="font-semibold text-blue-800">Deposit Slip file:</p>
              <p className="font-mono text-blue-700 mt-1">DS_[PAYMENT_REF_NO].jpg</p>
            </div>
            <div className="p-3 border rounded bg-green-50 border-green-200">
              <p className="font-semibold text-green-800">Provider OR file:</p>
              <p className="font-mono text-green-700 mt-1">OR_[PAYMENT_REF_NO].jpg</p>
            </div>
            <div className="p-3 border rounded bg-yellow-50 border-yellow-200 text-yellow-900">
              <p className="font-semibold flex items-center gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                Notes
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                <li>Allowed types: PDF, JPG, JPEG, PNG</li>
                <li>Maximum size per file: 10 MB</li>
                <li>PRN must exist in Settlement records</li>
                <li>Processing runs in background queue</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 bg-white rounded-lg shadow-md p-5">
          <h3 className="text-base font-bold text-gray-800 mb-4">Upload Files</h3>

          <div
            className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
              isDragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOver(false);
            }}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            <FontAwesomeIcon icon={faCloudUploadAlt} className="text-5xl mb-3 text-blue-500" />
            <p className="text-base font-semibold text-gray-700">Drag and drop files here</p>
            <p className="text-sm text-gray-500 mt-1">or click to browse multiple files</p>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleSelect}
              className="hidden"
            />
          </div>

          {files.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-700">
                  Selected Files: {files.length} ({totalSizeLabel})
                </p>
                <button onClick={clearAll} className="text-xs text-red-600 hover:text-red-700 font-medium">
                  Clear All
                </button>
              </div>

              <div className="max-h-56 overflow-y-auto border rounded-lg">
                {files.map((file, index) => (
                  <div key={`${file.name}_${file.size}_${index}`} className="px-3 py-2 border-b last:border-b-0 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-gray-400 hover:text-red-500"
                      title="Remove file"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faUpload} className={uploading ? "animate-spin" : ""} />
                  {uploading ? "Uploading..." : "Upload & Process"}
                </button>
                <button
                  onClick={clearAll}
                  disabled={uploading}
                  className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>
          )}

          {uploadResult && (
            <div className={`mt-5 rounded-lg p-4 border ${uploadResult.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={uploadResult.success ? faCheckCircle : faTimesCircle}
                  className={`text-2xl mt-0.5 ${uploadResult.success ? "text-green-600" : "text-red-600"}`}
                />
                <div className="flex-1">
                  <p className={`font-semibold ${uploadResult.success ? "text-green-800" : "text-red-800"}`}>
                    {uploadResult.success ? "Upload Successful" : "Upload Failed"}
                  </p>
                  <p className={`text-sm ${uploadResult.success ? "text-green-700" : "text-red-700"}`}>
                    {typeof uploadResult.message === "string" ? uploadResult.message : "Unexpected error"}
                  </p>

                  {uploadResult.success && uploadResult.uploadId && (
                    <>
                      <div className="mt-3 p-2.5 bg-white rounded border border-green-200">
                        <p className="text-xs text-gray-500">Upload ID</p>
                        <p className="font-mono text-xs text-gray-800 break-all">{uploadResult.uploadId}</p>
                        <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                          <FontAwesomeIcon icon={faInfoCircle} />
                          Uploaded files: {uploadResult.uploadedCount}
                        </p>
                      </div>

                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => navigate(`/depo-or-batch-upload-processed/${uploadResult.uploadId}`)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View Processed
                        </button>
                        <button
                          onClick={() => navigate("/depo-or-batch-upload-history")}
                          className="flex-1 bg-gray-700 hover:bg-gray-800 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faHistory} />
                          View History
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default DepoOrBatchUploadPage;
